import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight } from 'lucide-react';

// Use the same Shayamal image as homepage
const SHAYAMAL_IMAGE = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/vxkjfdze_new%20version.jpg";
const PRAKSHAL_IMAGE = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/x5xzw364_prakshal%20.jpeg";
const PARIKSHA_IMAGE = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/xyav9gk4_pariksha.jpg";

export default function AboutPage() {
  const teamRef = useRef(null);
  const isTeamInView = useInView(teamRef, { once: true, margin: "-100px" });

  const team = [
    {
      name: "Shayamal Vallabhjee",
      role: "Co-Founder & Human Performance Designer",
      image: SHAYAMAL_IMAGE,
      bio: "Over two decades working with world-class athletes at FC Barcelona, Olympic teams, and Fortune 500 executives. Faculty at Stanford Graduate School of Business."
    },
    {
      name: "Prakshal Jain",
      role: "Co-Founder, Brand & Strategy",
      image: PRAKSHAL_IMAGE,
      bio: "Strategic expertise in brand positioning and market dynamics. Years of experience building brands that create lasting impact."
    },
    {
      name: "Pariksha Thakur",
      role: "Operations",
      image: PARIKSHA_IMAGE,
      bio: "The operational backbone of Peak 9, ensuring every program and client engagement runs seamlessly."
    }
  ];

  return (
    <main data-testid="about-page">
      {/* Hero */}
      <section className="pt-24 lg:pt-32 pb-14 lg:pb-20 bg-[#1F2A44]">
        <div className="container-peak">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center px-4 sm:px-0"
          >
            <span className="section-tag block mb-4 lg:mb-6">About Us</span>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light leading-[1.1] mb-4 lg:mb-6 text-[#F4F1EC]">
              We help leaders and teams{' '}
              <em className="not-italic text-[#D97904]">perform at their peak</em>
            </h1>
            <p className="text-[#F4F1EC]/70 text-base lg:text-lg max-w-2xl mx-auto">
              Peak 9 was founded on a simple belief: sustainable high performance is possible when you engineer signal over noise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-14 lg:py-24 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="max-w-3xl mx-auto text-center px-4 sm:px-0">
            <span className="section-tag block mb-4 lg:mb-6">Our Mission</span>
            <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-light leading-[1.2] text-[#1F2A44] mb-4 lg:mb-6">
              To help individuals and organizations achieve extraordinary results while maintaining well-being.
            </h2>
            <p className="text-[#5A6B7A] text-sm lg:text-base leading-relaxed">
              We combine decades of experience working with elite athletes, world-class teams, and global leaders to deliver programs that create lasting transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Team - All in one row with circular images */}
      <section ref={teamRef} className="py-16 lg:py-24 bg-white">
        <div className="container-peak">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isTeamInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 lg:mb-16"
          >
            <span className="section-tag block mb-4 lg:mb-6">Our Team</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#1F2A44]">
              The people behind <em className="not-italic text-[#D97904]">Peak 9</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-12 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                animate={isTeamInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="text-center"
              >
                {/* Circular Image Container */}
                <div className="relative mb-6 mx-auto">
                  <div className="w-32 h-32 sm:w-36 sm:h-36 lg:w-44 lg:h-44 mx-auto relative">
                    {/* Decorative ring */}
                    <div className="absolute inset-0 rounded-full border-2 border-[#D97904]/20" />
                    <div className="absolute inset-1 rounded-full border border-[#E6E1DA]" />
                    {/* Image */}
                    <div className="absolute inset-2 rounded-full overflow-hidden bg-[#F4F1EC]">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
                
                <h3 className="font-['Cormorant_Garamond'] text-lg lg:text-xl text-[#1F2A44] mb-1">
                  {member.name}
                </h3>
                <p className="text-[#D97904] text-[10px] lg:text-xs font-['Syne'] uppercase tracking-wider mb-3">
                  {member.role}
                </p>
                <p className="text-[#5A6B7A] text-xs lg:text-sm leading-relaxed max-w-xs mx-auto">
                  {member.bio}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-24 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 lg:mb-16">
              <span className="section-tag block mb-4 lg:mb-6">Our Approach</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] text-[#1F2A44]">
                What sets us <em className="not-italic text-[#D97904]">apart</em>
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-6 lg:gap-8">
              <div className="text-center p-6 bg-white border border-[#E6E1DA]">
                <div className="text-4xl font-['Cormorant_Garamond'] text-[#D97904] mb-3">01</div>
                <h3 className="font-['Syne'] text-sm uppercase tracking-wider text-[#1F2A44] mb-2">Science-Backed</h3>
                <p className="text-[#5A6B7A] text-xs lg:text-sm">Every framework is grounded in research and tested at the highest levels.</p>
              </div>
              <div className="text-center p-6 bg-white border border-[#E6E1DA]">
                <div className="text-4xl font-['Cormorant_Garamond'] text-[#D97904] mb-3">02</div>
                <h3 className="font-['Syne'] text-sm uppercase tracking-wider text-[#1F2A44] mb-2">Results-Driven</h3>
                <p className="text-[#5A6B7A] text-xs lg:text-sm">We focus on measurable outcomes, not just inspiration.</p>
              </div>
              <div className="text-center p-6 bg-white border border-[#E6E1DA]">
                <div className="text-4xl font-['Cormorant_Garamond'] text-[#D97904] mb-3">03</div>
                <h3 className="font-['Syne'] text-sm uppercase tracking-wider text-[#1F2A44] mb-2">Sustainable</h3>
                <p className="text-[#5A6B7A] text-xs lg:text-sm">Peak performance that doesn't come at the cost of well-being.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-[#1F2A44]">
        <div className="container-peak">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] mb-4 lg:mb-6 text-[#F4F1EC]">
              Ready to work with us?
            </h2>
            <p className="text-[#F4F1EC]/70 text-sm lg:text-base mb-8 lg:mb-10">
              Let's discuss how we can help you and your team achieve peak performance.
            </p>
            <a
              href="https://calendar.app.google/evsn9LsubajagFF3A"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3"
            >
              Get in Touch
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
